import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Box, Typography, TextField, Button, Card } from "@mui/material";
import authService from "../../services/auth/authService";

const ResetPassword: React.FC = () => {
    const [searchParams] = useSearchParams();
    const resetToken = searchParams.get("token");
    const navigate = useNavigate();

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleResetPassword = async () => {
        setMessage(null);
        setError(null);

        if (!password || !confirmPassword) {
            setError("Please fill in all fields.");
            return;
        }

        if (password !== confirmPassword) {
            setError("Passwords do not match!");
            return;
        }

        setLoading(true);

        try {
            await authService.resetPassword(resetToken!, password);
            setMessage("Password reset successfully! Redirecting...");
            setTimeout(() => navigate("/login"), 3000);
        } catch (err) {
            setError("Failed to reset password. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#0D1117",
            }}
        >
            <Card
                sx={{
                    width: 400,
                    padding: 4,
                    backgroundColor: "#161B22",
                    borderRadius: 2,
                    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.5)",
                    textAlign: "center",
                }}
            >
                <Box sx={{ textAlign: "center", marginBottom: 3 }}>
                    <img
                        src="/logo.png"
                        alt="VCAT Logo"
                        style={{ width: "150px", marginBottom: "1rem" }}
                    />
                    <Typography variant="h5" sx={{ color: "#FFFFFF", fontWeight: "bold" }}>
                        Reset Password
                    </Typography>
                </Box>

                <TextField
                    label="New Password"
                    type="password"
                    fullWidth
                    margin="normal"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputLabelProps={{ style: { color: "#A8B2D1" } }}
                    InputProps={{
                        style: { color: "#FFFFFF" },
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "#A8B2D1" },
                            "&:hover fieldset": { borderColor: "#64FFDA" },
                            "&.Mui-focused fieldset": { borderColor: "#64FFDA" },
                        },
                    }}
                />
                <TextField
                    label="Confirm Password"
                    type="password"
                    fullWidth
                    margin="normal"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    InputLabelProps={{ style: { color: "#A8B2D1" } }}
                    InputProps={{
                        style: { color: "#FFFFFF" },
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "#A8B2D1" },
                            "&:hover fieldset": { borderColor: "#64FFDA" },
                            "&.Mui-focused fieldset": { borderColor: "#64FFDA" },
                        },
                    }}
                />

                {error && (
                    <Typography sx={{ color: "#FF6B6B", textAlign: "center", marginTop: 1 }}>
                        {error}
                    </Typography>
                )}
                {message && (
                    <Typography sx={{ color: "#64FFDA", textAlign: "center", marginTop: 1 }}>
                        {message}
                    </Typography>
                )}

                <Button
                    fullWidth
                    variant="contained"
                    sx={{
                        marginTop: 2,
                        backgroundColor: "#1F6FEB",
                        color: "#FFFFFF",
                        "&:hover": { backgroundColor: "#2C8AFF" },
                    }}
                    onClick={handleResetPassword}
                    disabled={loading}
                >
                    {loading ? "Resetting..." : "Reset Password"}
                </Button>
            </Card>
        </Box>
    );
};

export default ResetPassword;
